// Audio Alerts — Web Audio API synth, no external assets.
// Sounds for restaurant operations: kitchen (KDS) + waiter terminals.

const STORAGE_KEY = "smarttable_sound_muted";

let ctx = null;
let unlocked = false;

const getCtx = () => {
    if (ctx) return ctx;
    try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        ctx = new AC();
        return ctx;
    } catch (_e) {
        return null;
    }
};

// iOS / Chrome autoplay-policy: AudioContext must be resumed after a user gesture.
export const unlockAudio = () => {
    if (unlocked) return;
    const c = getCtx();
    if (!c) return;
    if (c.state === "suspended") {
        c.resume().catch(() => {});
    }
    unlocked = true;
};

// Persisted mute toggle
export const isMuted = () => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
};
export const setMuted = (v) => {
    try { localStorage.setItem(STORAGE_KEY, v ? "1" : "0"); } catch { /* */ }
    try { window.dispatchEvent(new Event("smarttable-sound-changed")); } catch { /* */ }
};

// --- Low-level helpers ---
const playTone = (c, freq, startOffset, duration, type = "sine", gainPeak = 0.25) => {
    const t0 = c.currentTime + startOffset;
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(gainPeak, t0 + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
    osc.connect(gain);
    gain.connect(c.destination);
    osc.start(t0);
    osc.stop(t0 + duration + 0.05);
};

const playSweep = (c, fromHz, toHz, startOffset, duration, gainPeak = 0.25) => {
    const t0 = c.currentTime + startOffset;
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(fromHz, t0);
    osc.frequency.linearRampToValueAtTime(toHz, t0 + duration);
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(gainPeak, t0 + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
    osc.connect(gain);
    gain.connect(c.destination);
    osc.start(t0);
    osc.stop(t0 + duration + 0.05);
};

const safePlay = (fn) => {
    if (isMuted()) return;
    const c = getCtx();
    if (!c) return;
    if (c.state === "suspended") c.resume().catch(() => {});
    try { fn(c); } catch { /* */ }
};

// --- Sound vocabulary ---

// KDS: nuova comanda — ding-dong (campanello) — due note sine pulite
export const dingDong = () => safePlay((c) => {
    playTone(c, 880, 0.0, 0.45, "sine", 0.32);
    playTone(c, 660, 0.30, 0.55, "sine", 0.30);
});

// KDS: variazione stato comanda — click morbido (acknowledge)
export const softClick = () => safePlay((c) => {
    playTone(c, 1200, 0.0, 0.10, "sine", 0.18);
});

// Cameriere: piatto pronto da ritirare — chime ascendente (C5-E5-G5)
export const chimeReady = () => safePlay((c) => {
    playTone(c, 523.25, 0.00, 0.20, "triangle", 0.28); // C5
    playTone(c, 659.25, 0.18, 0.20, "triangle", 0.28); // E5
    playTone(c, 783.99, 0.36, 0.40, "triangle", 0.30); // G5
});

// Cameriere: chiamata cliente — campanella urgente (3 squilli)
export const bellCustomerCall = () => safePlay((c) => {
    for (let i = 0; i < 3; i++) {
        playTone(c, 1320, i * 0.22, 0.16, "sine", 0.32);
        playTone(c, 990,  i * 0.22 + 0.10, 0.16, "sine", 0.26);
    }
});

// KDS: piatto in ritardo ≥20 min — beep "uh-oh" arancione (due tonalità ripetute)
export const beepWarning20 = () => safePlay((c) => {
    for (let i = 0; i < 2; i++) {
        playTone(c, 660, i * 0.45 + 0.00, 0.20, "square", 0.22);
        playTone(c, 520, i * 0.45 + 0.22, 0.22, "square", 0.22);
    }
});

// KDS: piatto in ritardo ≥30 min — sirena rossa (sweep ripetuto)
export const alarmCritical30 = () => safePlay((c) => {
    for (let i = 0; i < 3; i++) {
        playSweep(c, 400, 900, i * 0.55, 0.40, 0.30);
    }
});

// Cameriere/Cliente: piatto segnato TERMINATO dall'admin — doppio "blip" basso (avviso negativo)
export const blipTerminato = () => safePlay((c) => {
    playTone(c, 440, 0.00, 0.12, "square", 0.26);
    playTone(c, 330, 0.16, 0.18, "square", 0.26);
});

/**
 * Notifica vocale "Piatto da ritirare: [items] tavolo [N]" usata dal tablet del cameriere
 * quando il KDS preme EVASA. Usa Web Speech API (TTS). Se non disponibile, fallback su beep.
 * Rispetta il flag mute globale.
 */
export const speakPiattoPronto = (items, tableLabel) => {
    if (isMuted()) return;
    const itemsTxt = Array.isArray(items) && items.length > 0 ? items.join(", ") : "piatto";
    const tableTxt = tableLabel || "";
    const text = `Piatto da ritirare: ${itemsTxt}. ${tableTxt}`;
    try {
        if (typeof window !== "undefined" && "speechSynthesis" in window) {
            // Cancella eventuali messaggi in coda per evitare accumuli
            window.speechSynthesis.cancel();
            const utter = new SpeechSynthesisUtterance(text);
            utter.lang = "it-IT";
            utter.rate = 1.05;
            utter.pitch = 1.0;
            utter.volume = 1.0;
            // Suona prima un dingDong corto per attirare attenzione
            dingDong();
            // Poi parla dopo 600ms
            setTimeout(() => {
                try { window.speechSynthesis.speak(utter); } catch (_e) { /* */ }
            }, 600);
            return;
        }
    } catch (_e) { /* */ }
    // Fallback: doppio dingDong se TTS non disponibile
    dingDong();
    setTimeout(() => dingDong(), 700);
};

// Convenience to attach global gesture-unlock
export const installGestureUnlock = () => {
    const fn = () => { unlockAudio(); };
    window.addEventListener("click", fn, { once: false, passive: true });
    window.addEventListener("touchstart", fn, { once: false, passive: true });
    window.addEventListener("keydown", fn, { once: false });
};
